import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Loader2, Dumbbell, Clock3, CalendarDays, Layers, CheckCircle2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const TIERS = [
  { key: 'debutant', label: 'Débutant', labelEn: 'Beginner', color: 'text-emerald-400 border-emerald-500/25 bg-emerald-500/10' },
  { key: 'intermediaire', label: 'Intermédiaire', labelEn: 'Intermediate', color: 'text-sky-400 border-sky-500/25 bg-sky-500/10' },
  { key: 'avance', label: 'Avancé', labelEn: 'Advanced', color: 'text-amber-400 border-amber-500/25 bg-amber-500/10' },
  { key: 'pro', label: 'Pro', labelEn: 'Pro', color: 'text-fuchsia-400 border-fuchsia-500/25 bg-fuchsia-500/10' },
];

/** Picker client des 20 "Programmes Standardisés Body-Up" — l'option la plus rapide pour
 * démarrer (aucune attente : pas d'IA, pas de coach), copie modifiable immédiatement. */
export const StandardProgramsPicker = ({ en, authFetch, onBack, onAssigned }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [templates, setTemplates] = useState(null);
  const [locked, setLocked] = useState(false);
  const [preview, setPreview] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    authFetch('/api/tracking/programs-pro/standard-templates')
      .then((r) => r.json())
      .then((d) => { setLocked(!!d.locked); setTemplates(d.templates || []); })
      .catch(() => { setTemplates([]); toast.error(L('Échec du chargement.', 'Failed to load.')); });
  }, [authFetch]);

  const assign = (template) => {
    setAssigning(true);
    authFetch(`/api/tracking/programs-pro/standard-templates/${template.template_id}/assign`, { method: 'POST' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        toast.success(L('Programme ajouté ! Votre coach a été prévenu.', 'Program added! Your coach has been notified.'));
        onAssigned(d.program);
      })
      .catch(() => toast.error(L("Échec de l'ajout du programme.", 'Failed to add program.')))
      .finally(() => setAssigning(false));
  };

  if (templates === null) {
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="standard-programs-picker">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="standard-programs-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <div>
        <h2 className="text-xl font-black text-white flex items-center gap-2"><Layers className="w-5 h-5 text-amber-400" /> {L('Programmes Standard Body-Up', 'Body-Up Standard Programs')}</h2>
        <p className="text-sm text-zinc-400 mt-1">{L('Choisissez un programme prêt à l\'emploi, conçu par Body-Up selon votre niveau — disponible instantanément, modifiable ensuite comme vous le souhaitez.', 'Pick a ready-made program, designed by Body-Up for your level — instantly available, fully editable afterwards.')}</p>
      </div>

      {locked ? (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/25 p-5 text-center" data-testid="standard-programs-locked">
          <p className="text-white font-bold">{L('Réservé aux membres Suivi Pro+ et plus', 'Suivi Pro+ members and above only')}</p>
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-8">{L('Aucun programme standard disponible pour le moment.', 'No standard program available yet.')}</p>
      ) : (
        <div className="space-y-6">
          {TIERS.map((tier) => {
            const items = templates.filter((t) => t.tier === tier.key);
            if (items.length === 0) return null;
            return (
              <div key={tier.key}>
                <p className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border mb-2.5 ${tier.color}`}>
                  {L(tier.label, tier.labelEn)}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {items.map((t) => (
                    <button key={t.template_id} onClick={() => setPreview(t)}
                      className="text-left rounded-2xl border border-white/10 bg-zinc-800/50 overflow-hidden hover:border-white/25 transition-colors"
                      data-testid={`standard-template-card-${t.template_id}`}>
                      {t.cover_image_url && (
                        <div className="w-full h-28 overflow-hidden">
                          <img src={t.cover_image_url} alt="" loading="lazy" className="w-full h-full object-cover" data-testid={`standard-template-cover-${t.template_id}`} />
                        </div>
                      )}
                      <div className="p-4">
                        <p className="font-bold text-white">{t.name}</p>
                        <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{t.goal}</p>
                        <div className="flex items-center gap-3 mt-2.5 text-[11px] text-zinc-500">
                          <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> {t.sessions?.length || 0} {L('séances', 'sessions')}</span>
                          <span className="flex items-center gap-1"><Clock3 className="w-3 h-3" /> {t.weeks_target} {L('sem.', 'wks')}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {preview && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm" onClick={() => setPreview(null)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed inset-x-0 bottom-0 sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-[91] bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[85vh] sm:max-w-md sm:w-full"
              data-testid="standard-template-preview">
              <div className="max-h-[85vh] overflow-y-auto p-6">
              {preview.cover_image_url && (
                <div className="w-full h-36 rounded-2xl overflow-hidden -mt-1 mb-4">
                  <img src={preview.cover_image_url} alt="" className="w-full h-full object-cover" data-testid="standard-template-preview-cover" />
                </div>
              )}
              <h3 className="text-lg font-black text-white">{preview.name}</h3>
              <p className="text-sm text-zinc-400 mt-1.5">{preview.goal}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {preview.weeks_target} {L('semaines', 'weeks')}</span>
                <span className="flex items-center gap-1.5"><Dumbbell className="w-3.5 h-3.5" /> {preview.days_per_week}x/{L('sem.', 'wk')}</span>
              </div>
              <div className="mt-4 space-y-2">
                {(preview.sessions || []).map((s, i) => (
                  <div key={i} className="rounded-xl bg-zinc-800/60 border border-white/10 px-3.5 py-2.5">
                    <p className="text-sm font-semibold text-white">{s.session_label}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{(s.exercises || []).map((e) => e.name).join(', ')}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => assign(preview)} disabled={assigning}
                className="w-full mt-5 inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-50"
                data-testid="standard-template-assign-btn">
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {L('Choisir ce programme', 'Choose this program')}
              </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StandardProgramsPicker;
